import { useGlobalNotification } from "@expert/common-ui";
import { getLogger } from "@expert/logging";
import { Flex, Group, Stack } from "@mantine/core";
import PQueue from "p-queue";
import { type JSX, useEffect } from "react";
import { usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { sdkEventBus, useAgentSdk, useCallbackState, type VoiceTask } from "@/sdk";
import { CallbackInfoBanner } from "../ScheduleCallback";
import { TransferCall } from "../TransferCall/TransferCall";
import { useContactsStateStore } from "../TransferCall/vdnContacts.store";
import { verifyAndPlayWhisper } from "../utils/whisperUtils";
import classes from "./CallControls.module.css";
import { CallControlsSwitch } from "./CallControlsSwitch";
import { CallDetailsSelector } from "./CallDetailsSelector";
import { useControlsStore } from "./controls.store";

const BUFFER_INTERVAL = 300;
const dialQueue = new PQueue({ concurrency: 1, intervalCap: 1, interval: BUFFER_INTERVAL });

interface CallControlsProps {
    voiceTask: VoiceTask;
}

const logger = getLogger({ module: "CallControls" });

export function CallControls({ voiceTask }: CallControlsProps): JSX.Element {
    const agentSdk = useAgentSdk();
    const callbackState = useCallbackState();
    const globalNotification = useGlobalNotification();
    const { transferListEnabled: isTransferListEnabled } = usePartnerFeatureConfigured();

    const {
        clearPanelsState,
        dialpadInput,
        transferCallActive,
        dialpadNotification,
        setIsConference,
        setDialpadNotification,
        resetState: resetControlsStoreState,
    } = useControlsStore();

    const { getAndSetContacts } = useContactsStateStore();

    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    useEffect(() => {
        let isUnmounted = false;

        if (!isTransferListEnabled) {
            logger.info("Transfer list is disabled for partner via AppConfig. Clearing contacts.");
            useContactsStateStore.getState().resetState();
            return;
        }

        const fetchContacts = async () => {
            try {
                await getAndSetContacts(voiceTask.partner);
                if (!isUnmounted) {
                    logger.info("Contacts successfully set");
                }
            } catch (error) {
                if (!isUnmounted) {
                    logger.error({ error }, "Error getting contacts:");
                }
            }
        };

        void fetchContacts();

        return () => {
            isUnmounted = true;
        };
    }, [isTransferListEnabled, voiceTask.partner]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    useEffect(() => {
        if (["wrapping", "completed", "cancelled"].includes(voiceTask.status)) {
            clearPanelsState();
            setIsConference(false);

            if (dialpadNotification) {
                globalNotification.clearNotification(dialpadNotification);
                setDialpadNotification(undefined);
            }
        }

        // Whenever the main task is in a pending state, that means we're accepting a call
        // and it has to be the first call in this session (since a conference can't have been started yet).
        // If that's the case, then we reset the call controls state here so the call controls always
        // start in the default state at the beginning of a call
        if (voiceTask.status === "pending") {
            resetControlsStoreState();
        }
    }, [voiceTask.status]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    useEffect(() => {
        if (dialpadInput.length > 0) {
            const dial = dialpadInput[dialpadInput.length - 1];
            void dialQueue.add(() => agentSdk.sendDtmfDigits(dial));
        }
    }, [dialpadInput]);

    useEffect(() => {
        const unsubCallAccepted = sdkEventBus.on("call_accepted", (newVoiceTask: VoiceTask) => {
            verifyAndPlayWhisper(newVoiceTask.whisper);
        });

        return () => {
            unsubCallAccepted();
        };
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    useEffect(() => {
        if (voiceTask.conferenceParticipants.some(({ type }) => type !== "inactive")) {
            clearPanelsState();
            setIsConference(true);
        } else {
            setIsConference(false);
        }
    }, [voiceTask.conferenceParticipants]);

    return (
        <Stack data-testid="active-task" gap="xs">
            <Group className={classes.controlsRow}>
                <CallDetailsSelector task={voiceTask} />
                <Flex flex="1" justify="flex-end" miw="fit-content">
                    <Group align="center" data-testid={`call-controls-group-${voiceTask.status}`} justify="flex-end">
                        <CallControlsSwitch task={voiceTask} />
                    </Group>
                </Flex>
            </Group>
            {callbackState && voiceTask.status === "assigned" ? <CallbackInfoBanner /> : null}
            {transferCallActive ? <TransferCall voiceTask={voiceTask} /> : null}
        </Stack>
    );
}
